import React, { useCallback, useEffect, useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { saveBlob } from '../../utils/download';
import * as fmt from '../../lib/format';
import InfoTip from '../InfoTip';
import { HALF_LABEL, KeptFile } from './types';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const ICON = 'inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

const size = (n: number): string => (n < 1024 ? `${fmt.count(n)} B` : `${fmt.count(Math.round(n / 1024))} KB`);

// The copies that were kept when a report was sent: each is exactly the file that went out, so it never changes while the
// page above it stays live. An admin may remove one.
export default function KeptCopies({ month, isAdmin, reloadKey }: { month: string; isAdmin: boolean; reloadKey: number }) {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [files, setFiles] = useState<KeptFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getTechReportFiles(month);
      if (!r.success) throw new Error(r.error);
      setFiles(r.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not read the kept copies');
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const download = async (f: KeptFile) => {
    try {
      saveBlob(await apiService.downloadTechReportFile(f.id), f.filename);
    } catch (e: any) {
      showToast('error', e?.message || 'Could not download the copy');
    }
  };

  const remove = async (f: KeptFile) => {
    const yes = await confirm({ title: `Remove ${f.filename}?`, message: 'It is the record of what was sent, and it cannot be put back.', confirmLabel: 'Remove copy' });
    if (!yes) return;
    try {
      const r = await apiService.deleteTechReportFile(f.id);
      if (!r.success) throw new Error(r.error);
      showToast('success', 'Copy removed');
      void load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not remove the copy');
    }
  };

  return (
    <section className={`${CARD} mb-5 p-5 print:hidden`} aria-label="Kept copies">
      <h2 className="flex items-center text-base font-semibold text-gray-900">Copies kept for {fmt.month(month)}<InfoTip id="report.kept" /></h2>
      {error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}{' '}<button type="button" onClick={() => void load()} className="font-medium underline">Try again</button></p>}
      {!error && files && files.length === 0 && <p className="mt-2 text-sm text-gray-600">Nothing kept yet. Use "Keep a copy" when you send the report.</p>}
      {files && files.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {files.map((f) => (
            <li key={f.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
              <span className="min-w-0 break-words text-gray-900">
                {f.filename} <span className="text-gray-600">· {HALF_LABEL[f.half]}</span>
              </span>
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600">
                <span>{fmt.dateTime(f.kept_at)}{f.kept_by ? ` by ${f.kept_by}` : ''} · {size(f.bytes)}</span>
                <button type="button" onClick={() => download(f)} className={ICON} aria-label={`Download ${f.filename}`}><Download className="h-3.5 w-3.5" aria-hidden />Download</button>
                {isAdmin && <button type="button" onClick={() => remove(f)} className={ICON} aria-label={`Remove ${f.filename}`}><Trash2 className="h-3.5 w-3.5" aria-hidden />Remove</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
